/**
 * TranslatorEngine — shared translation primitives for subtitle-translator
 * and voice-translator tools.
 *
 * Plain browser script: sets `globalThis.TranslatorEngine`. Node unit tests
 * load it via the `vm` module (see tests/js/translator-engine.test.mjs)
 * because the project's package.json has "type": "module", which blocks
 * require() on plain .js files.
 *
 * Design principles:
 *  - Pure functions (detectLanguage, detectLanguageInfo, splitByTags,
 *    decodeEntities, splitForApi, classifyHttpError, toChromeCode) have NO
 *    DOM, NO fetch, NO timers. They are fully unit-testable.
 *  - I/O functions (translateSegment, translateEntryText, detectLanguageAsync,
 *    createConcurrentQueue) accept an injectable `fetch` via `opts.fetch`
 *    and an `AbortSignal`, so tests can mock network without monkey-
 *    patching globals.
 *  - Retries happen inside translateSegment. Callers don't need a
 *    retry loop; they just call once per segment.
 *
 * Multi-provider (v3): translateEntryText resolves an ORDERED CHAIN of
 * translation backends per language pair and tries them in turn, so one
 * provider being exhausted / rate-limited / unreachable no longer fails
 * the file — the next provider in the chain transparently takes over.
 *   1. Chrome/Edge built-in Translator API (`window.Translator`) — on-
 *      device, free, no quota, no network, works offline. When present it
 *      is used ALONE (an HTTP fallback would defeat those properties).
 *   2. Google public endpoint (translate_a/single) — CORS-enabled, no key,
 *      no hard daily char cap, keyed by the user's own IP so load spreads
 *      across users. Primary HTTP provider for non-Chrome browsers.
 *   3. MyMemory free API — secondary HTTP net. A registered contact email
 *      (MYMEMORY_EMAIL) lifts the anonymous 5k chars/day to 50k chars/day,
 *      but even 50k is less than one feature-length subtitle file, which is
 *      precisely why it sits BEHIND Google rather than carrying the tool.
 * A provider that returns an endpoint-level failure (quota / rate_limit /
 * network) is marked dead for the rest of the run so a 1500-entry file
 * doesn't re-hit a dead endpoint 1500 times.
 *
 * Unit tests have no Translator global; they pass `opts.provider` to pin a
 * specific HTTP provider, or a host-discriminating `opts.fetch` to exercise
 * the chain deterministically.
 *
 * Many texts (v4, 2026-10-13): translateEntryText is one request per text —
 * right for a live phrase, wrong for a file. A document, a subtitle file or
 * the captions of a photo go through translateTexts, which sends them in
 * batches (Google's translate_a/t: up to a thousand texts in one POST) and
 * falls back to the per-text chain behind a circuit breaker. The measurements
 * that made this the rule are in the comment above translateTexts and in
 * docs/why/primitives/translate-texts.md.
 */
(function (global) {
    'use strict';

    const API_URL = 'https://api.mymemory.translated.net/get';

    // MyMemory's per-query limit is ~500 chars. 450 leaves margin for the
    // encoded query string and prevents boundary-case 414 errors.
    const API_MAX_CHARS = 450;

    // Registered contact email sent as MyMemory's `de` parameter. It lifts
    // the free quota from 5000 chars/day (anonymous) to 50000 chars/day.
    // NOTE: the limit is CHARACTERS, not words — and 50k chars is roughly a
    // single short film's worth of dialogue, so MyMemory alone cannot carry
    // whole-movie subtitle translation. That is why it is the SECONDARY
    // provider behind Google in the chain. Callers may override per-request
    // via `opts.email` ('' opts out of the de param entirely).
    const MYMEMORY_EMAIL = 'translate@timbrica.com';

    // HTML entity whitelist. Covers everything MyMemory ever returns
    // (quotes, apostrophes, ampersands) plus numeric refs for completeness.
    // Intentionally NOT DOM-based — keeps the module pure and prevents any
    // chance of parsing actual tags if a bug ever let them through.
    const NAMED_ENTITIES = {
        amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
        laquo: '«', raquo: '»', ldquo: '“', rdquo: '”',
        lsquo: '‘', rsquo: '’', hellip: '…', mdash: '—', ndash: '–',
    };
    function decodeEntities(s) {
        if (!s || typeof s !== 'string' || s.indexOf('&') === -1) return s;
        return s
            .replace(/&#x([0-9a-fA-F]+);/g, (_, n) => {
                const cp = parseInt(n, 16);
                return Number.isFinite(cp) ? String.fromCodePoint(cp) : _;
            })
            .replace(/&#(\d+);/g, (_, n) => {
                const cp = Number(n);
                return Number.isFinite(cp) ? String.fromCodePoint(cp) : _;
            })
            .replace(/&([a-zA-Z]+);/g, (m, name) =>
                Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, name) ? NAMED_ENTITIES[name] : m
            );
    }

    // Minimum character count per script to consider it "dominant". A handful
    // of proper nouns in another script shouldn't flip the detection.
    const DETECT_MIN_CHARS = 5;

    // Unicode-range detector. Distinguishes non-Latin scripts reliably.
    //
    // LIMITATION: Latin-script languages (en/es/fr/de/it/pt/nl/pl/...) share
    // the same Unicode range and cannot be distinguished by character codes
    // alone. For those, detectLanguageInfo returns { lang:'en', ambiguous:true }
    // so callers can either ask the user to confirm or fall through to the
    // async Chrome LanguageDetector (detectLanguageAsync).
    const DETECT_SCRIPTS = {
        cyrillic:   /[Ѐ-ӿ]/g,
        arabic:     /[؀-ۿݐ-ݿ]/g,
        hebrew:     /[֐-׿]/g,
        greek:      /[Ͱ-Ͽ]/g,
        devanagari: /[ऀ-ॿ]/g,
        bengali:    /[ঀ-৿]/g,
        thai:       /[฀-๿]/g,
        hangul:     /[가-힯ᄀ-ᇿ]/g,
        hiragana:   /[぀-ゟ]/g,
        katakana:   /[゠-ヿ]/g,
        cjk:        /[一-鿿]/g,
    };

    // Detect the dominant script and map it to a language code.
    // Returns { lang, ambiguous }. `ambiguous` is true only when detection
    // fell back to the Latin default ('en') — meaning the real language
    // could be any Latin-script language and the caller should not trust it.
    //
    // DOMINANT means dominant. Until 2026-10-13 the first script with more than
    // DETECT_MIN_CHARS characters won outright, so one Russian word in the
    // sample («Москва» — six letters) turned an English document into a Russian
    // one. On /document-translate that is a dead end rather than a wrong guess:
    // the page's target is Russian, the document "is Russian too", and the
    // visitor is asked which OTHER language they want. Now a non-Latin script
    // has to outweigh the Latin letters next to it. Ideographs and syllable
    // blocks carry about a word each, so they count three to a Latin letter's
    // one — a Chinese manual full of English product names is still Chinese.
    const DETECT_LATIN = /[A-Za-zÀ-ÖØ-öø-ɏ]/g;
    const DETECT_DENSE_WEIGHT = 3;
    // A script is not a language. Latin is written by English, German,
    // French, Spanish, Polish, Turkish, Vietnamese…; Cyrillic by Russian,
    // Ukrainian, Kazakh, Bulgarian, Serbian; Arabic letters by Arabic, Persian
    // and Urdu; Devanagari by Hindi, Marathi, Nepali. For these the `lang`
    // below is only the most likely language of the script, so the answer is
    // marked `ambiguous` and must not be handed to a translator as the source:
    // identifyLanguage() resolves it. A script with one language of its own
    // (kana, hangul, Greek, Hebrew, Thai, Bengali) answers itself.
    //
    // ⚠️ Until 2026-10-23 Cyrillic and Arabic were reported as certain. A
    // Ukrainian document on /ru was "Russian, the same as the target" and the
    // visitor was asked which OTHER language to translate it into; a Persian
    // one was translated "from Arabic".
    const SHARED_SCRIPTS = new Set(['latin', 'cyrillic', 'arabic', 'devanagari']);
    const LANG_SCRIPT = {
        ja: 'japanese', ko: 'hangul', 'zh-CN': 'han', ru: 'cyrillic', ar: 'arabic',
        he: 'hebrew', el: 'greek', th: 'thai', hi: 'devanagari', bn: 'bengali',
    };
    function detectLanguageInfo(text) {
        const r = _detectScriptLanguage(text);
        r.ambiguous = !!r.script && SHARED_SCRIPTS.has(r.script);
        return r;
    }
    function _detectScriptLanguage(text) {
        if (!text || typeof text !== 'string') return { lang: 'en', script: null };
        const sample = text.slice(0, 4000);
        const c = {};
        for (const k of Object.keys(DETECT_SCRIPTS)) {
            c[k] = (sample.match(DETECT_SCRIPTS[k]) || []).length;
        }
        const latin = (sample.match(DETECT_LATIN) || []).length;
        const kana = c.hiragana + c.katakana;
        // [lang, characters that prove the script is there, weight against Latin].
        // Order is the tie-break. Japanese: kana presence beats CJK-only (which
        // would be Chinese), and its ideographs count towards it.
        const candidates = [
            ['ja', kana, (kana + c.cjk) * DETECT_DENSE_WEIGHT],
            ['ko', c.hangul, c.hangul * DETECT_DENSE_WEIGHT],
            ['zh-CN', c.cjk, c.cjk * DETECT_DENSE_WEIGHT],
            ['ru', c.cyrillic, c.cyrillic],
            ['ar', c.arabic, c.arabic],
            ['he', c.hebrew, c.hebrew],
            ['el', c.greek, c.greek],
            ['th', c.thai, c.thai],
            ['hi', c.devanagari, c.devanagari],
            ['bn', c.bengali, c.bengali],
        ];
        let best = null;
        for (const cand of candidates) {
            if (cand[1] <= DETECT_MIN_CHARS) continue;
            if (!best || cand[2] > best[2]) best = cand;
        }
        if (best && best[2] > latin) return { lang: best[0], script: LANG_SCRIPT[best[0]] };
        // No letters at all («12 345», «—») is not "Latin": nothing to say.
        return { lang: 'en', script: latin ? 'latin' : null };
    }
    // The script's most likely language as a bare code. ⚠️ For Latin,
    // Cyrillic and Arabic text this is a GUESS ('en', 'ru', 'ar'), never a
    // source language for a translator — see SHARED_SCRIPTS. A caller that
    // needs the language of a text asks identifyLanguage(); the guard
    // LanguageGuessGoesThroughIdentifyTest keeps it that way.
    function detectLanguage(text) {
        return detectLanguageInfo(text).lang;
    }

    // ─── Chrome built-in AI: Translator + LanguageDetector ──────────
    //
    // Chrome 138+ ships an on-device translation model exposed as the
    // global `Translator` (and `LanguageDetector`). When present it is
    // strictly better than MyMemory: no quota, no network round-trip,
    // works offline once the model is downloaded. Everything below is
    // feature-detected — absent in Node, Firefox, Safari, and old Chrome,
    // where the engine transparently falls back to MyMemory.

    function _chromeTranslatorUsable() {
        return typeof global.Translator !== 'undefined' &&
            global.Translator && typeof global.Translator.create === 'function';
    }
    function _chromeDetectorUsable() {
        return typeof global.LanguageDetector !== 'undefined' &&
            global.LanguageDetector && typeof global.LanguageDetector.create === 'function';
    }

    // Map an application language code to the BCP-47 form Chrome's
    // Translator API expects. Region subtags are dropped except for
    // Chinese, where script (Hans/Hant) is meaningful. Returns null for
    // 'auto' / empty — the Chrome path requires an explicit source.
    function toChromeCode(code) {
        if (!code || typeof code !== 'string') return null;
        const lc = code.toLowerCase();
        if (lc === 'auto') return null;
        if (lc === 'zh-cn' || lc === 'zh' || lc === 'zh-hans') return 'zh';
        if (lc === 'zh-tw' || lc === 'zh-hant') return 'zh-Hant';
        return lc.split('-')[0];
    }

    // Promise that rejects with AbortError when `signal` fires, otherwise
    // resolves/rejects with the wrapped promise. Used to make the Chrome
    // translator (which historically ignored AbortSignal) cancellable.
    function raceAbort(promise, signal) {
        if (!signal) return promise;
        if (signal.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
        return new Promise((resolve, reject) => {
            function onAbort() { reject(new DOMException('Aborted', 'AbortError')); }
            signal.addEventListener('abort', onAbort, { once: true });
            promise.then(
                (v) => { signal.removeEventListener('abort', onAbort); resolve(v); },
                (e) => { signal.removeEventListener('abort', onAbort); reject(e); }
            );
        });
    }

    // Reject after `ms` if `promise` has not settled. The Chrome built-in
    // AI surfaces (`Translator`, `LanguageDetector`) can be *present* yet
    // hang indefinitely — headless Chromium, restricted enterprise/webview
    // contexts, a model that never finishes provisioning. Without this
    // guard `resolveProvider` never settles and the caller hangs forever
    // (subtitle-translator awaits it before every run).
    function withTimeout(promise, ms, label) {
        return new Promise((resolve, reject) => {
            let settled = false;
            const timer = setTimeout(() => {
                if (settled) return;
                settled = true;
                reject(new Error((label || 'operation') + ' timed out'));
            }, ms);
            promise.then(
                (v) => { if (!settled) { settled = true; clearTimeout(timer); resolve(v); } },
                (e) => { if (!settled) { settled = true; clearTimeout(timer); reject(e); } }
            );
        });
    }
    // Availability is a cheap capability check — fail fast. create() may
    // legitimately download a model, so it gets a longer leash.
    const CHROME_AVAIL_TIMEOUT_MS = 3000;
    const CHROME_CREATE_TIMEOUT_MS = 12000;

    // Build a Chrome on-device translator for one pair, or null if the
    // browser cannot serve it. The created translator is wrapped in the
    // same `{ name, translatePart }` provider shape as the MyMemory path.
    async function _buildChromeProvider(src, tgt, options) {
        if (!_chromeTranslatorUsable()) return null;
        const cs = toChromeCode(src), ct = toChromeCode(tgt);
        if (!cs || !ct || cs === ct) return null;
        let avail;
        try {
            avail = await withTimeout(
                global.Translator.availability({ sourceLanguage: cs, targetLanguage: ct }),
                CHROME_AVAIL_TIMEOUT_MS, 'Translator.availability'
            );
        } catch (_) {
            return null; // unsupported code, or a hung/blocked API — treat as unavailable
        }
        if (!avail || avail === 'unavailable') return null;

        const createOpts = { sourceLanguage: cs, targetLanguage: ct };
        // 'downloadable' / 'downloading' → create() pulls the model. Surface
        // progress so the UI can show a one-time "preparing offline model".
        if (options && typeof options.onModelDownload === 'function') {
            createOpts.monitor = (m) => {
                m.addEventListener('downloadprogress', (e) => {
                    try { options.onModelDownload(e.loaded || 0); } catch (_) {}
                });
            };
        }
        let translator;
        try {
            translator = await withTimeout(
                Promise.resolve(global.Translator.create(createOpts)),
                CHROME_CREATE_TIMEOUT_MS, 'Translator.create'
            );
        } catch (_) {
            return null;
        }
        return {
            name: 'chrome',
            async translatePart(value, signal) {
                if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
                const out = await raceAbort(Promise.resolve(translator.translate(value)), signal);
                return decodeEntities(typeof out === 'string' ? out : value);
            },
        };
    }

    // Per-pair provider cache. translateEntryText is called once per
    // subtitle entry / phrase; without caching we'd re-probe Chrome
    // availability (and rebuild the translator) thousands of times.
    const _providerCache = new Map();
    // The same pairs, once their probe has settled: { key → provider | null }.
    // Read synchronously by order:'network-first', which must not wait.
    const _providerSettled = new Map();

    // ─── Which language is this text in ─────────────────────────────
    //
    // The source language a translator is TOLD decides the translation more
    // than anything else in this file. Report 2026-10-22, /ru/document-
    // translate, a German .xlsx: «Очень кривой перевод с немецкого на
    // русский». The tool had put «English» into «Язык документа» — the script
    // detector answers 'en' for any Latin text — and Google, told the text is
    // English, does not translate German: «Kursbeschreibung» → «Курсбешрайбунг»,
    // «Teilnehmer» → «Тейлнемер», «die Prüfung» → «умереть Прюфунг»; of 1500
    // German interface strings 302 came back untranslated and most of the rest
    // transliterated. Told 'de', all 1500 were translated.
    //
    // Measured 2026-10-23 on 28 languages (Wikipedia intros and titles, and
    // ~28 000 short interface strings per language from our own lang files —
    // the closest thing to spreadsheet cells), sampled the way the document
    // tool samples:
    //   · Google, the sample as one text with sl=auto — 99.9 % on interface
    //     strings, 98.9 % from just THREE short strings, 98–100 % on prose;
    //   · on-device libraries (franc, eld, tinyld): 89–95 % on ten short
    //     strings, ~50–60 % on three — not good enough to name the source;
    //   · Chrome's LanguageDetector exists in ~18 % of the document tool's
    //     sessions (none in Yandex Browser) — a fallback, not the answer.
    // Google is also the service that will translate, so its reading of the
    // language is the one the translation agrees with.
    //
    // MIXED DOCUMENTS. A vocabulary list — German word, Russian word — reads
    // as Russian when taken whole (0 of 16), and on /ru that is the dead end
    // «the document is already in the target language». When the whole-text
    // answer IS the target, each text of the sample is asked separately and a
    // second language holding ≥ 15 % of the letters in ≥ 3 texts is the one to
    // translate from (16 of 16 vocabulary lists). Google leaves the target-
    // language cells as they are when told the other language (measured), so
    // one source per document is enough. Below those thresholds the document
    // is the target language: three short Russian lines read one by one come
    // back as Bulgarian or Serbian now and then, and must not become «from
    // Bulgarian».
    //
    // Per-text sl=auto during translation is NOT the alternative: a short cell
    // on its own is guessed badly — «ja» → Estonian «и», «gut» → English «кишка»,
    // «bestanden» → Dutch «файлы».
    //
    // Answer: { lang, ambiguous, script, source } — `ambiguous: true` means
    // nobody could tell, and the caller keeps «auto» (Google then guesses per
    // text, which is worse than knowing and far better than a wrong 'en').
    const IDENTIFY_SAMPLE_CHARS = 4000;
    const IDENTIFY_TEXT_CHARS = 300;
    const IDENTIFY_TIMEOUT_MS = 8000;
    const IDENTIFY_OTHER_MIN_SHARE = 0.15;
    const IDENTIFY_OTHER_MIN_TEXTS = 3;

    // Google's answer → our codes. It says 'iw' for Hebrew and 'jw' for
    // Javanese; Chinese keeps its script.
    function normalizeLangCode(code) {
        if (!code || typeof code !== 'string') return null;
        const lc = code.trim().toLowerCase();
        if (!lc || lc === 'auto' || lc === 'und') return null;
        if (lc === 'zh-cn' || lc === 'zh-hans' || lc === 'zh') return 'zh-CN';
        if (lc === 'zh-tw' || lc === 'zh-hant') return 'zh-TW';
        if (lc === 'iw') return 'he';
        if (lc === 'jw') return 'jv';
        return lc.split('-')[0];
    }
    function _sameLanguage(a, b) {
        const x = normalizeLangCode(a), y = normalizeLangCode(b);
        return !!x && !!y && x.split('-')[0] === y.split('-')[0];
    }

    // One POST to the batch door with sl=auto: every `q` comes back as
    // [translation, detectedLanguage]. Only the language is used. A missing
    // or null language (seen 2026-10-23 on a whole run of batches) is "do
    // not know", never a language.
    async function _googleDetectOnce(texts, tgt, signal, fetchFn, timeoutMs) {
        const body = new URLSearchParams();
        for (const t of texts) body.append('q', t);
        const url = GOOGLE_BATCH_URL + '?' + new URLSearchParams({
            client: 'gtx', sl: 'auto', tl: toGoogleCode(tgt) || 'en',
        }).toString();
        let resp, data = null;
        try {
            await withDeadline(async (sig) => {
                resp = await fetchFn(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
                    body: body.toString(),
                    signal: sig,
                });
                if (resp.ok) { try { data = await resp.json(); } catch (_) { /* non-JSON */ } }
            }, signal, timeoutMs);
        } catch (err) {
            if (err && err.name === 'AbortError') throw err;
            return null;
        }
        if (!resp || !resp.ok || !Array.isArray(data) || data.length !== texts.length) return null;
        return data.map((d) => (Array.isArray(d) ? normalizeLangCode(d[1]) : null));
    }

    // The second language of a mixed sample: weight by letters, not texts —
    // a one-word cell must not outvote a paragraph.
    function _secondLanguage(texts, langs, first) {
        const w = new Map(), n = new Map();
        let total = 0;
        texts.forEach((t, i) => {
            const letters = (String(t).match(/\p{L}/gu) || []).length;
            total += letters;
            const l = langs[i];
            if (!l || _sameLanguage(l, first)) return;
            w.set(l, (w.get(l) || 0) + letters);
            n.set(l, (n.get(l) || 0) + 1);
        });
        let best = null;
        for (const [l, letters] of w) {
            if ((n.get(l) || 0) < IDENTIFY_OTHER_MIN_TEXTS) continue;
            if (!best || letters > best.letters) best = { lang: l, letters: letters };
        }
        if (!best || !total || best.letters / total < IDENTIFY_OTHER_MIN_SHARE) return null;
        return { lang: best.lang, share: Math.round(1000 * best.letters / total) / 1000 };
    }

    async function _chromeDetect(text) {
        if (!_chromeDetectorUsable()) return null;
        try {
            const detector = await withTimeout(
                Promise.resolve(global.LanguageDetector.create()),
                CHROME_CREATE_TIMEOUT_MS, 'LanguageDetector.create'
            );
            const results = await withTimeout(
                Promise.resolve(detector.detect(String(text || '').slice(0, IDENTIFY_SAMPLE_CHARS))),
                CHROME_AVAIL_TIMEOUT_MS, 'LanguageDetector.detect'
            );
            if (results && results[0] && results[0].detectedLanguage &&
                (results[0].confidence == null || results[0].confidence > 0.5)) {
                return normalizeLangCode(results[0].detectedLanguage);
            }
        } catch (_) { /* unavailable or hung */ }
        return null;
    }

    /**
     * identifyLanguage(texts, opts) → { lang, ambiguous, script, source, share? }
     *
     * `texts` — the document's own texts (an array; a string is split into
     * lines). `opts.target` — the language it will be translated into: only
     * with it can a mixed document be told apart from one already in the
     * target. `opts.signal`, `opts.fetch`, `opts.timeoutMs`; `opts.network:
     * false` keeps the answer on the device.
     */
    async function identifyLanguage(texts, opts) {
        const o = opts || {};
        const list = (Array.isArray(texts) ? texts : String(texts || '').split(/\n+/));
        const units = [];
        let chars = 0;
        for (const raw of list) {
            if (chars >= IDENTIFY_SAMPLE_CHARS) break;
            const s = String(raw == null ? '' : raw).replace(/\s+/g, ' ').trim().slice(0, IDENTIFY_TEXT_CHARS);
            if (!s || !/\p{L}/u.test(s)) continue;
            units.push(s);
            chars += s.length + 1;
        }
        const joined = units.join(' ').slice(0, IDENTIFY_SAMPLE_CHARS);
        const sync = detectLanguageInfo(joined);
        if (!sync.ambiguous) return { lang: sync.lang, ambiguous: false, script: sync.script, source: 'script' };

        const fetchFn = o.fetch || (typeof fetch !== 'undefined' ? fetch : null);
        const tgt = o.target && o.target !== 'auto' ? o.target : null;
        if (fetchFn && o.network !== false) {
            const timeoutMs = o.timeoutMs || IDENTIFY_TIMEOUT_MS;
            const whole = await _googleDetectOnce([joined], tgt || 'en', o.signal, fetchFn, timeoutMs);
            const first = whole && whole[0];
            if (first) {
                if (tgt && _sameLanguage(first, tgt) && units.length >= IDENTIFY_OTHER_MIN_TEXTS) {
                    const each = await _googleDetectOnce(units, tgt, o.signal, fetchFn, timeoutMs);
                    const second = each ? _secondLanguage(units, each, first) : null;
                    if (second) {
                        return { lang: second.lang, ambiguous: false, script: sync.script, source: 'google-mixed', share: second.share };
                    }
                }
                return { lang: first, ambiguous: false, script: sync.script, source: 'google' };
            }
        }
        const chrome = await _chromeDetect(joined);
        if (chrome) return { lang: chrome, ambiguous: false, script: sync.script, source: 'chrome' };
        return { lang: sync.lang, ambiguous: true, script: sync.script, source: 'script' };
    }

    // The single-text form other tools already call: same answer, same
    // shape ({ lang, ambiguous, … }). Until 2026-10-23 it asked only Chrome's
    // LanguageDetector, so outside Chrome every Latin text was 'en'.
    async function detectLanguageAsync(text, opts) {
        return identifyLanguage(text, opts);
    }

    // Split text into [{type:'tag'|'text', value}] so HTML/SSA markup
    // (<i>, <b>, <font color=...>, {\an8}, {\c&H...&}) passes through
    // the translation API untouched.
    const TAG_PATTERN = /<\/?[a-zA-Z][^>]*>|\{[^}]*\}/g;
    function splitByTags(text) {
        if (!text) return [];
        const parts = [];
        let last = 0, m;
        TAG_PATTERN.lastIndex = 0;
        while ((m = TAG_PATTERN.exec(text)) !== null) {
            if (m.index > last) parts.push({ type: 'text', value: text.slice(last, m.index) });
            parts.push({ type: 'tag', value: m[0] });
            last = m.index + m[0].length;
        }
        if (last < text.length) parts.push({ type: 'text', value: text.slice(last) });
        return parts;
    }

    // Split long text at safe boundaries: newline > sentence-end > whitespace.
    // Chunks are rejoined without delimiters added/removed, so original
    // formatting (line breaks, spacing) round-trips when the parts are
    // concatenated back.
    function splitForApi(text, maxChars) {
        const limit = maxChars || API_MAX_CHARS;
        if (text.length <= limit) return [text];
        const chunks = [];
        let remaining = text;
        while (remaining.length > limit) {
            let cut = remaining.lastIndexOf('\n', limit);
            if (cut < limit / 2) {
                const sentence = remaining.slice(0, limit).match(/.*[.!?…](?:\s|$)/s);
                cut = sentence ? sentence[0].length : -1;
            }
            if (cut < limit / 2) cut = remaining.lastIndexOf(' ', limit);
            if (cut <= 0) cut = limit;
            chunks.push(remaining.slice(0, cut));
            remaining = remaining.slice(cut);
        }
        if (remaining) chunks.push(remaining);
        return chunks;
    }

    class TranslationError extends Error {
        constructor(reason, message) {
            super(message);
            this.name = 'TranslationError';
            this.reason = reason;
        }
    }

    // Map HTTP status + MyMemory error body → reason token. Callers show a
    // localized toast based on `reason`; they should not parse the message.
    //
    // Real MyMemory quota message (observed):
    //   "MYMEMORY WARNING: YOU USED ALL AVAILABLE FREE TRANSLATIONS FOR TODAY.
    //    NEXT AVAILABLE IN …"
    // It comes back with HTTP 200 and responseStatus != 200, which is why we
    // need body-sniffing in addition to status codes.
    const QUOTA_BODY_PATTERN = /quota|daily\s*limit|used\s+all|free\s+translations?/i;
    function classifyHttpError(status, data) {
        // Body-sniff first — MyMemory returns quota errors under HTTP 200 AND under
        // 429; the body is the only unambiguous signal. If we trusted status alone,
        // a quota-exhausted response with status 429 would be classified as
        // 'rate_limit' and retried, wasting further quota attempts.
        if (data && typeof data.responseDetails === 'string' &&
            QUOTA_BODY_PATTERN.test(data.responseDetails)) {
            return 'quota';
        }
        if (status === 429) return 'rate_limit';
        if (status === 403) return 'quota';
        if (status >= 500) return 'network';
        return 'unknown';
    }

    // Abort-safe sleep. Resolves after `ms` or rejects with AbortError.
    function sleep(ms, signal) {
        return new Promise((resolve, reject) => {
            if (signal && signal.aborted) {
                reject(new DOMException('Aborted', 'AbortError'));
                return;
            }
            const timer = setTimeout(() => {
                if (signal) signal.removeEventListener('abort', onAbort);
                resolve();
            }, ms);
            function onAbort() {
                clearTimeout(timer);
                reject(new DOMException('Aborted', 'AbortError'));
            }
            if (signal) signal.addEventListener('abort', onAbort, { once: true });
        });
    }

    // A request that never answers must not hold its worker for ever. Before
    // 2026-10-13 no fetch in this file had a deadline: a connection the
    // network had quietly dropped parked one of the four workers until the
    // browser gave up (minutes), and a run whose four workers were all parked
    // looked exactly like a hung tool. The deadline covers the BODY as well as
    // the headers — `fn` gets a signal that fires on the caller's abort or on
    // the timer, whichever comes first, and a timeout surfaces as an ordinary
    // 'network' failure so the provider chain treats it like any other outage.
    const SINGLE_TIMEOUT_MS = 12000;
    async function withDeadline(fn, signal, ms) {
        if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
        const ctl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
        let timer = null, timedOut = false;
        const onAbort = () => { if (ctl) ctl.abort(); };
        if (signal) signal.addEventListener('abort', onAbort, { once: true });
        try {
            return await new Promise((resolve, reject) => {
                timer = setTimeout(() => {
                    timedOut = true;
                    if (ctl) ctl.abort();
                    reject(new TranslationError('network', 'timeout after ' + ms + ' ms'));
                }, ms);
                Promise.resolve().then(() => fn(ctl ? ctl.signal : signal)).then(resolve, reject);
            });
        } catch (err) {
            // Our own abort makes fetch reject with AbortError; the caller did
            // not cancel anything, so it must not read as a cancellation.
            if (timedOut) throw new TranslationError('network', 'timeout after ' + ms + ' ms');
            throw err;
        } finally {
            clearTimeout(timer);
            if (signal) signal.removeEventListener('abort', onAbort);
        }
    }

    const DEFAULT_RETRIES = 2;
    const DEFAULT_BACKOFF_MS = 500;
    // Reasons that are pointless to retry — hitting quota again won't help.
    const NON_RETRYABLE = new Set(['quota']);

    async function _translateOnce(text, src, tgt, signal, fetchFn, email, timeoutMs) {
        const params = {
            q: text,
            langpair: src + '|' + tgt,
        };
        // A contact email lifts MyMemory's daily quota ~10×. Skipped only
        // when a caller explicitly passes opts.email === '' (e.g. tests).
        if (email) params.de = email;
        const url = API_URL + '?' + new URLSearchParams(params).toString();

        let resp, data = null;
        try {
            await withDeadline(async (sig) => {
                resp = await fetchFn(url, { signal: sig });
                try { data = await resp.json(); } catch (_) { /* non-JSON body */ }
            }, signal, timeoutMs || SINGLE_TIMEOUT_MS);
        } catch (err) {
            if (err && err.name === 'AbortError') throw err;
            if (err instanceof TranslationError) throw err;
            throw new TranslationError('network', (err && err.message) || 'fetch failed');
        }

        if (!resp.ok) {
            throw new TranslationError(classifyHttpError(resp.status, data), 'HTTP ' + resp.status);
        }
        // MyMemory sometimes returns responseStatus as a string — normalize.
        const apiStatus = data ? Number(data.responseStatus) : NaN;
        const translated = data && data.responseData && data.responseData.translatedText;
        if (apiStatus !== 200 || !translated) {
            throw new TranslationError(
                classifyHttpError(Number.isFinite(apiStatus) ? apiStatus : 0, data),
                'API status ' + apiStatus
            );
        }
        return decodeEntities(translated);
    }

    // Translate one segment (≤ API_MAX_CHARS, no tags) via MyMemory. Retries
    // transient failures with exponential backoff; propagates quota errors
    // immediately.
    async function translateSegment(text, src, tgt, signal, opts) {
        const options = opts || {};
        const fetchFn = options.fetch || (typeof fetch !== 'undefined' ? fetch : null);
        if (!fetchFn) throw new Error('TranslatorEngine: no fetch available');
        if (!text || !text.trim()) return text;

        const retries = options.retries != null ? options.retries : DEFAULT_RETRIES;
        const baseBackoffMs = options.baseBackoffMs != null ? options.baseBackoffMs : DEFAULT_BACKOFF_MS;
        // `email: ''` is an explicit opt-out; `undefined` means use the default.
        const email = options.email != null ? options.email : MYMEMORY_EMAIL;

        let attempt = 0, lastErr = null;
        while (attempt <= retries) {
            if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
            try {
                return await _translateOnce(text, src, tgt, signal, fetchFn, email, options.timeoutMs);
            } catch (err) {
                if (err && err.name === 'AbortError') throw err;
                lastErr = err;
                const reason = err && err.reason;
                if (reason && NON_RETRYABLE.has(reason)) throw err;
                attempt++;
                if (attempt > retries) break;
                // Exponential backoff: 500ms, 1000ms, 2000ms, ...
                await sleep(baseBackoffMs * Math.pow(2, attempt - 1), signal);
            }
        }
        throw lastErr;
    }

    // MyMemory provider: chunk a prose part to the API char limit and
    // translate each chunk. Mirrors the historical behaviour — a failing
    // chunk keeps its original text and the first error reason surfaces.
    function _buildMyMemoryProvider(src, tgt, options) {
        return {
            name: 'mymemory',
            async translatePart(value, signal) {
                const chunks = splitForApi(value);
                const translated = new Array(chunks.length);
                let firstError = null;
                for (let j = 0; j < chunks.length; j++) {
                    try {
                        translated[j] = await translateSegment(chunks[j], src, tgt, signal, options);
                    } catch (err) {
                        if (err && err.name === 'AbortError') throw err;
                        if (!firstError) firstError = err;
                        translated[j] = chunks[j];
                    }
                }
                if (firstError) {
                    const e = new TranslationError(firstError.reason || 'unknown', firstError.message);
                    e.partial = translated.join('');
                    throw e;
                }
                return translated.join('');
            },
        };
    }

    // ─── Google public translate endpoint (translate_a/single) ─────
    //
    // The same free, keyless endpoint used by translate-shell and many FOSS
    // tools. It answers with CORS `Access-Control-Allow-Origin: *` (so the
    // browser can call it directly), has no hard daily character cap, and —
    // because every request carries the *user's* IP — spreads load across
    // users instead of pooling on one key the way MyMemory's contact email
    // does. It is undocumented and can change, which is exactly why it lives
    // in a CHAIN with MyMemory behind it rather than as a lone dependency.
    const GOOGLE_URL = 'https://translate.googleapis.com/translate_a/single';
    // GET budget: `q` is URL-encoded (~3× for non-Latin scripts). 1500 raw
    // chars stays well under practical URL-length limits even fully encoded.
    const GOOGLE_MAX_CHARS = 1500;

    // Map an app language code to what the Google endpoint expects. Our
    // codes are already Google-style (en, de, ru, pt, zh-CN, zh-TW); only a
    // couple diverge. 'auto' is a valid source for this endpoint.
    function toGoogleCode(code) {
        if (!code || typeof code !== 'string') return null;
        const lc = code.toLowerCase();
        if (lc === 'auto') return 'auto';
        if (lc === 'zh-cn' || lc === 'zh' || lc === 'zh-hans') return 'zh-CN';
        if (lc === 'zh-tw' || lc === 'zh-hant') return 'zh-TW';
        if (lc === 'jv') return 'jw';   // Javanese: endpoint uses the legacy 'jw'
        if (lc === 'he') return 'iw';   // Hebrew: endpoint uses the legacy 'iw'
        return lc;
    }

    // Parse translate_a/single's nested-array response. Shape:
    //   [[[translatedChunk, originalChunk, …], …], …]
    // The full translation is every chunk's [0] concatenated, which keeps
    // Google's own sentence/newline segmentation intact. Returns null when
    // the body isn't the expected shape so the caller can fall through.
    function _parseGoogleResponse(data) {
        if (!Array.isArray(data) || !Array.isArray(data[0])) return null;
        let out = '';
        for (const seg of data[0]) {
            if (seg && typeof seg[0] === 'string') out += seg[0];
        }
        return out;
    }

    // 429/403 → the endpoint is throttling/blocking this IP (treat the whole
    // provider as down for the run); 5xx / fetch-throw → network. A 200 with
    // an unparseable body is 'unknown' (one-off, do NOT kill the provider).
    function _classifyGoogleStatus(status) {
        if (status === 429 || status === 403) return 'rate_limit';
        if (status >= 500) return 'network';
        return 'unknown';
    }

    async function _googleTranslateOnce(text, src, tgt, signal, fetchFn, timeoutMs) {
        if (!text || !text.trim()) return text;
        const sl = toGoogleCode(src) || 'auto';
        const tl = toGoogleCode(tgt);
        const url = GOOGLE_URL + '?' + new URLSearchParams({
            client: 'gtx', sl: sl, tl: tl, dt: 't', q: text,
        }).toString();

        let resp, data = null;
        try {
            await withDeadline(async (sig) => {
                resp = await fetchFn(url, { signal: sig });
                if (resp.ok) { try { data = await resp.json(); } catch (_) { /* non-JSON body */ } }
            }, signal, timeoutMs || SINGLE_TIMEOUT_MS);
        } catch (err) {
            if (err && err.name === 'AbortError') throw err;
            if (err instanceof TranslationError) throw err;
            throw new TranslationError('network', (err && err.message) || 'fetch failed');
        }
        if (!resp.ok) {
            throw new TranslationError(_classifyGoogleStatus(resp.status), 'HTTP ' + resp.status);
        }
        const translated = _parseGoogleResponse(data);
        if (translated == null) {
            throw new TranslationError('unknown', 'unparseable google response');
        }
        return decodeEntities(translated);
    }

    // One Google chunk with a single transient retry, so a momentary 5xx /
    // rate blip doesn't condemn the whole provider for a long file. An
    // 'unknown' (unparseable 200) is not retried — it won't fix itself.
    async function _googleSegment(text, src, tgt, signal, fetchFn, retries, timeoutMs) {
        const max = retries == null ? 1 : retries;
        let attempt = 0, lastErr = null;
        while (attempt <= max) {
            if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
            try {
                return await _googleTranslateOnce(text, src, tgt, signal, fetchFn, timeoutMs);
            } catch (err) {
                if (err && err.name === 'AbortError') throw err;
                lastErr = err;
                if (err && err.reason === 'unknown') break;
                attempt++;
                if (attempt > max) break;
                await sleep(300 * attempt, signal);
            }
        }
        throw lastErr;
    }

    // Google provider: chunk an over-long prose part to the GET budget and
    // translate each chunk. Mirrors the MyMemory provider's shape so the two
    // are interchangeable in the chain — a failing chunk keeps its original
    // text and the first error surfaces with the best-effort partial.
    function _buildGoogleProvider(src, tgt, options) {
        const opts = options || {};
        const fetchFn = opts.fetch || (typeof fetch !== 'undefined' ? fetch : null);
        return {
            name: 'google',
            async translatePart(value, signal) {
                if (!fetchFn) throw new TranslationError('network', 'no fetch available');
                const chunks = splitForApi(value, GOOGLE_MAX_CHARS);
                const translated = new Array(chunks.length);
                let firstError = null;
                for (let j = 0; j < chunks.length; j++) {
                    try {
                        translated[j] = await _googleSegment(chunks[j], src, tgt, signal, fetchFn, opts.googleRetries, opts.timeoutMs);
                    } catch (err) {
                        if (err && err.name === 'AbortError') throw err;
                        if (!firstError) firstError = err;
                        translated[j] = chunks[j];
                    }
                }
                if (firstError) {
                    const e = new TranslationError(firstError.reason || 'unknown', firstError.message);
                    e.partial = translated.join('');
                    throw e;
                }
                return translated.join('');
            },
        };
    }

    // Resolve the ORDERED provider chain for a language pair. Chrome's
    // on-device translator wins outright when it supports the pair (used
    // alone — no network/quota/offline tradeoff). Otherwise an HTTP chain:
    // Google first (quality, no char cap, IP-distributed), MyMemory behind
    // it as the secondary net. `opts.provider` pins a single provider
    // (tests + manual override).
    //
    // `provider:'chrome'` is the on-device-ONLY pin: it resolves to the
    // built-in translator or to an EMPTY chain — deliberately no HTTP
    // fallback. /prompt-generator uses it because its need is "translate
    // this prompt at zero cost on the user's machine, or don't translate at
    // all": going out to Google/MyMemory for one prompt buys a quota, a
    // round-trip and an extra hop for text the browser can do itself, and
    // the caller already has a working second path (its cloud "improve"
    // lane returns English anyway). An empty chain is honest — the caller
    // gets `reason` and decides — while a silent HTTP fallback would make
    // "runs on your device" untrue without anyone noticing.
    async function _resolveProviders(src, tgt, options) {
        if (options && options.provider === 'mymemory') return [_buildMyMemoryProvider(src, tgt, options)];
        if (options && options.provider === 'google') return [_buildGoogleProvider(src, tgt, options)];

        const key = src + '|' + tgt;
        if (!_providerCache.has(key)) {
            const built = _buildChromeProvider(src, tgt, options);
            _providerCache.set(key, built);
            built.then((v) => { _providerSettled.set(key, v || null); }, () => { _providerSettled.set(key, null); });
        }
        let chrome = null;
        // `order:'network-first'` — a phrase the person will READ as the
        // translation (the final line of /voice-translator): Google first, the
        // browser's own model as the net when the network refuses, then
        // MyMemory. Measured 23.10.2026 on sentences: Google better in 40 of 100,
        // the on-device model in 8 (chrF 66.5 / 63.0 en→ru), and the model is not
        // faster on a sentence (160–355 ms). Live drafts keep the model first.
        // The model joins the chain only if it is already built — waiting for
        // its availability probe (up to 3 + 12 s) would delay the phrase.
        if (options && options.order === 'network-first' && !options.provider) {
            chrome = _providerSettled.get(key) || null;
            const chain = [_buildGoogleProvider(src, tgt, options)];
            if (chrome) chain.push(chrome);
            chain.push(_buildMyMemoryProvider(src, tgt, options));
            return chain;
        }
        try { chrome = await _providerCache.get(key); } catch (_) { chrome = null; }
        if (chrome) return [chrome];
        if (options && options.provider === 'chrome') return [];

        return [
            _buildGoogleProvider(src, tgt, options),
            _buildMyMemoryProvider(src, tgt, options),
        ];
    }

    // Resolve just the head provider's name for a pair without translating —
    // lets a caller tune concurrency/throttling (Chrome and Google are
    // fine at higher concurrency; MyMemory needs spacing).
    async function resolveProviderName(src, tgt, opts) {
        const providers = await _resolveProviders(src, tgt, opts || {});
        return providers[0] ? providers[0].name : 'mymemory';
    }

    // Endpoint-level failure reasons: the whole provider is unusable for the
    // rest of this run (quota burned, IP rate-limited, host/network down) —
    // as opposed to a one-off bad response for a single text. Seeing one
    // marks the provider dead for the run so a thousand-entry file doesn't
    // re-hit a dead endpoint a thousand times; the run rides the next
    // provider in the chain instead.
    const ENDPOINT_DEAD_REASONS = new Set(['quota', 'rate_limit', 'network']);

    // Translate one subtitle entry / phrase: split by tags, translate each
    // prose part through the provider CHAIN (first live provider that
    // succeeds wins), reassemble with tags in place. Returns
    // { text, provider, reason?, err_msg?, status? }. When `reason` is
    // present every provider failed for at least one part, `text` is the
    // ORIGINAL (untranslated), and the caller must mark this entry failed —
    // it must NOT present the original as a finished translation.
    //
    // Per-run provider health lives on the caller's `opts` object
    // (subtitle-translator passes ONE opts for the whole file, so a dead
    // endpoint is skipped for every remaining entry). Callers that pass no
    // opts — voice-translator's live phrases — get a fresh set per call:
    // correct, just without cross-phrase memory.
    async function translateEntryText(text, src, tgt, signal, opts) {
        if (!text || !text.trim()) return { text: text || '' };
        const options = opts || {};
        const parts = splitByTags(text);
        const out = new Array(parts.length);
        let firstError = null;

        const providers = await _resolveProviders(src, tgt, options);
        // An EMPTY chain is only reachable via `provider:'chrome'` (on-device
        // pin, no HTTP fallback). It must surface as a failure, not as a
        // success that happens to return the input: every part below would
        // fall through to `out[i] = p.value` with `partErr === null`, so
        // `firstError` would stay null and the caller would receive the
        // ORIGINAL text with no `reason` — i.e. an untranslated string
        // presented as a finished translation. Caught by reading the fall-
        // through, not by a failing test: nothing threw.
        if (!providers.length) {
            return { text, provider: 'none', reason: 'unsupported_pair', err_msg: 'no provider for ' + src + '→' + tgt, status: null };
        }
        const dead = options._deadProviders || (options._deadProviders = new Set());
        let usedProvider = providers[0] ? providers[0].name : 'unknown';

        for (let i = 0; i < parts.length; i++) {
            if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
            const p = parts[i];
            if (p.type === 'tag' || !p.value.trim()) {
                out[i] = p.value;
                continue;
            }

            // Live providers first; if every provider is marked dead (total
            // outage) try them all again anyway — a later entry might catch a
            // recovered endpoint instead of silently keeping originals.
            let chain = providers.filter(pr => !dead.has(pr.name));
            if (!chain.length) chain = providers;

            let partErr = null, done = false;
            for (const provider of chain) {
                try {
                    out[i] = await provider.translatePart(p.value, signal);
                    usedProvider = provider.name;
                    partErr = null;
                    done = true;
                    break;
                } catch (err) {
                    if (err && err.name === 'AbortError') throw err;
                    partErr = err;
                    if (err && ENDPOINT_DEAD_REASONS.has(err.reason)) dead.add(provider.name);
                    // fall through to the next provider in the chain
                }
            }

            if (!done) {
                if (!firstError) firstError = partErr;
                // Keep the best-effort partial the last provider produced,
                // else the original — never drop the line (timing must survive).
                out[i] = (partErr && typeof partErr.partial === 'string') ? partErr.partial : p.value;
            }
        }

        if (firstError) {
            // Phase Y (2026-06-04): pass through err_msg so /pino/streams gets
            // an actionable diagnostic instead of an opaque `translate_unknown`
            // (1469 events/7d pre-fix had empty err_msg — the message was
            // discarded HERE at the engine boundary). Truncate inline; callers
            // pass it straight to telemetry.
            return {
                text,
                provider: usedProvider,
                reason: firstError.reason || 'unknown',
                err_msg: String((firstError && firstError.message) || firstError || '').slice(0, 240),
                status: (firstError && (firstError.status || (firstError.response && firstError.response.status))) || null,
            };
        }
        return { text: stripInjectedZw(text, out.join(''), tgt), provider: usedProvider };
    }

    // ─── Many texts at once: translateTexts ─────────────────────────
    //
    // translateEntryText spends ONE HTTP request on ONE text. That is right for
    // a live phrase and wrong for a file: a document is hundreds to thousands
    // of paragraphs (30 days of /document-translate, measured 2026-10-13:
    // median 291 distinct paragraphs, p75 1065, p90 3811), and the public
    // Google endpoint answers a per-paragraph stream by slowing every request
    // down (~285 ms each, four at a time) and, after roughly 1200 of them,
    // by refusing the address altogether — in a browser the refusal has no CORS
    // header, so it arrives as a bare «Failed to fetch». Past that point the
    // run was left with MyMemory (5–50 thousand characters a DAY) and every
    // remaining paragraph climbed the whole retry ladder of two dead providers.
    // Field result: documents of 1000+ paragraphs took 6.5 minutes at the
    // median and 86 at p90, 30 % of them were abandoned, and 15 % of ALL runs
    // handed back a document with untranslated paragraphs in it.
    //
    // The same Google service has a batch door, translate_a/t: many `q` in one
    // POST, an array of translations back, in order. Measured the same day from
    // a real browser: 300 paragraphs come back word-for-word identical to the
    // per-paragraph answers (300 of 300); a 714 000-character document (p90)
    // is 24 requests and 12–23 seconds; ~1.8 million characters went through
    // without one refusal; and an address already refused by the per-text
    // endpoint was still served by this one. A request costs about the same
    // 1.5 s whether it carries 5 000 characters or 30 000, so batches are as
    // large as was measured to be safe rather than "politely" small.
    //
    // Order of work for one call:
    //   1. Chrome's on-device translator ALONE only when pinned
    //      (provider:'chrome') or with no network; otherwise it is step 2b —
    //      measured worse than the batch in every pair (23.10.2026, below).
    //   2. Google batch. A failed batch waits and retries (the wait is cheap:
    //      one batch is up to a thousand paragraphs); a batch door that stays
    //      shut is marked dead for the run.
    //   2b. What the batch left — the browser's own translator, when it
    //      serves the pair (no quota, no address to refuse).
    //   3. Whatever is still untranslated goes text by text through the old
    //      chain (Google per-text → MyMemory) behind a CIRCUIT BREAKER: a
    //      provider that reports an endpoint-level failure rests for a growing
    //      cool-down instead of being hit again by every remaining text, and
    //      when every provider is resting the run waits for the first to wake
    //      — within a fixed patience budget. When the budget is spent the rest
    //      FAILS FAST with its reason, so a run that cannot be finished ends
    //      in about half a minute and says so, instead of grinding for an hour.
    //
    // The waits are short on purpose. A refusal by address does not lift in a
    // minute (measured: the per-text endpoint was still refusing 40 minutes
    // later), so patience buys back only what is actually transient — a
    // network blip, a soft throttle of a few seconds. `opts.onStatus` tells the
    // caller when the run is waiting rather than working, so the screen can say
    // so instead of showing a bar that has stopped.
    //
    // Returns one { text, provider, reason?, err_msg? } per input text, in
    // order — the same contract as translateEntryText: `reason` present means
    // `text` is the ORIGINAL and the caller must show it as untranslated.
    const GOOGLE_BATCH_URL = 'https://translate.googleapis.com/translate_a/t';
    const BATCH_MAX_CHARS = 30000;      // measured OK up to 60 000 (313 KB body, Cyrillic)
    const BATCH_MAX_ITEMS = 1000;       // measured OK up to 2 013
    const BATCH_Q_MAX_CHARS = 5000;     // one `q`; a longer paragraph is cut at sentence ends
    const BATCH_CONCURRENCY = 3;
    const BATCH_TIMEOUT_MS = 30000;         // slowest measured answer: 3.9 s for 30 000 characters
    const BATCH_RETRY_DELAYS_MS = [1500, 4000];
    const FALLBACK_CONCURRENCY = 4;
    const FALLBACK_COOLDOWN_MS = 6000;      // doubles on every further strike, capped below
    const FALLBACK_COOLDOWN_MAX_MS = 60000;
    const FALLBACK_PATIENCE_MS = 20000;     // total time a run may spend waiting for a rested provider

    // translate_a/t answers with one entry per `q`: a plain string when the
    // source language was named, [text, detectedLang] when it was 'auto'.
    // Anything else — or a different COUNT — is a shape we do not understand,
    // and the caller must not guess which translation belongs to which text.
    function _parseGoogleBatch(data, expected) {
        if (!Array.isArray(data) || data.length !== expected) return null;
        const out = new Array(expected);
        for (let i = 0; i < expected; i++) {
            const v = Array.isArray(data[i]) ? data[i][0] : data[i];
            if (typeof v !== 'string') return null;
            out[i] = decodeEntities(v);
        }
        return out;
    }

    async function _googleBatchOnce(cores, src, tgt, signal, fetchFn, timeoutMs) {
        const sl = toGoogleCode(src) || 'auto';
        const tl = toGoogleCode(tgt);
        const body = new URLSearchParams();
        for (const c of cores) body.append('q', c);
        const url = GOOGLE_BATCH_URL + '?' + new URLSearchParams({ client: 'gtx', sl: sl, tl: tl }).toString();

        let resp, data = null;
        try {
            await withDeadline(async (sig) => {
                // A form-encoded POST is a CORS "simple request": no preflight.
                resp = await fetchFn(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
                    body: body.toString(),
                    signal: sig,
                });
                if (resp.ok) { try { data = await resp.json(); } catch (_) { /* non-JSON body */ } }
            }, signal, timeoutMs || BATCH_TIMEOUT_MS);
        } catch (err) {
            if (err && err.name === 'AbortError') throw err;
            if (err instanceof TranslationError) throw err;
            throw new TranslationError('network', (err && err.message) || 'fetch failed');
        }
        if (!resp.ok) {
            throw new TranslationError(_classifyGoogleStatus(resp.status), 'HTTP ' + resp.status);
        }
        const out = _parseGoogleBatch(data, cores.length);
        if (!out) throw new TranslationError('unknown', 'unparseable google batch response');
        return out;
    }

    // Pack distinct cores into requests: by characters and by count, in the
    // order they appear in the document.
    function _packBatches(items, maxChars, maxItems) {
        const out = [];
        let cur = [], chars = 0;
        for (const it of items) {
            const len = it.core.length;
            if (cur.length && (chars + len > maxChars || cur.length >= maxItems)) {
                out.push(cur); cur = []; chars = 0;
            }
            cur.push(it);
            chars += len;
        }
        if (cur.length) out.push(cur);
        return out;
    }

    // One text → its tag parts → for every prose part the pieces that travel:
    // { lead, core, trail }. The endpoint trims each `q`, and a part cut out
    // from between two tags («Hello <b>world</b> again») carries the spaces
    // that keep the words apart — so the whitespace is taken off here and put
    // back around the translation, instead of being trusted to survive.
    function _planText(text, maxCore) {
        const parts = splitByTags(text);
        const plan = [];
        for (const p of parts) {
            if (p.type === 'tag' || !p.value.trim()) { plan.push({ raw: p.value }); continue; }
            const pieces = p.value.length > maxCore ? splitForApi(p.value, maxCore) : [p.value];
            for (const piece of pieces) {
                const core = piece.trim();
                if (!core) { plan.push({ raw: piece }); continue; }
                const at = piece.indexOf(core);
                plan.push({ lead: piece.slice(0, at), core: core, trail: piece.slice(at + core.length) });
            }
        }
        return plan;
    }

    /**
     * Google's translation inserts ZERO WIDTH SPACE (U+200B), usually two in a
     * row, into 1–2 % of the sentences it returns: «станцию<U+200B><U+200B>до». Measured
     * 04.11.2026 on 900 NTREX sentences: 5 from Google, 0 from gpt-5.6-luna and
     * Claude, 0 in the sources. On a web page it is invisible; in a DOCX the
     * word cannot be found by search; in a PDF the face draws it with an
     * advance — the gap after «станцию» in every translated manual. It is taken
     * out unless the source had it or the target writes words without spaces
     * (Thai, Khmer, Lao, Burmese mark word boundaries with U+200B).
     */
    const ZW_KEEP_TARGETS = /^(th|km|lo|my)(\b|[-_])/i;
    function stripInjectedZw(src, out, tgt) {
        if (typeof out !== 'string' || (out.indexOf('\u200b') === -1 && out.indexOf('\ufeff') === -1)) return out;
        if (ZW_KEEP_TARGETS.test(String(tgt || '')) || String(src || '').indexOf('\u200b') !== -1) {
            return out.replace(/\ufeff/g, '');
        }
        return out.replace(/[\u200b\ufeff]+/g, '');
    }

    async function translateTexts(texts, src, tgt, signal, opts) {
        const options = opts || {};
        const list = Array.isArray(texts) ? texts : [];
        const results = new Array(list.length);
        const stats = options._stats || (options._stats = {});
        stats.lane = null; stats.requests = 0; stats.retries = 0;
        stats.batches = 0; stats.batchFailed = 0; stats.fallbackTexts = 0; stats.failed = 0; stats.empty = 0; stats.deviceTexts = 0;
        const onProgress = typeof options.onProgress === 'function' ? options.onProgress : null;
        const status = (state) => {
            if (typeof options.onStatus === 'function') { try { options.onStatus({ state: state }); } catch (_) {} }
        };
        if (!list.length) return results;
        if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');

        // ── 1. on-device FIRST only when pinned, or with no network ─────
        //
        // ⚠️ Until 23.10.2026 the browser's own translator came first whenever
        // it served the pair. Measured that day against human references from
        // our lang files (chrF, Chrome 156, 200 short strings + 100 sentences
        // per pair): Google beat it in all six slices — de→ru 54.6 / 50.7 and
        // 62.7 / 59.1, en→ru 60.9 / 59.1 and 66.5 / 63.0, fr→ru 55.9 / 50.8 and
        // 61.6 / 58.1; per string Google was better 2–5× as often (German cells:
        // «Teilnehmer» → «Абонент», «Gesamtbetrag» → «общее количество»,
        // «Note» → «Внимание»). It is also one text at a time — 160–355 ms a
        // sentence, against one batch for a whole document. So for a FILE it is
        // the fallback after the batch door, not the head; a phrase
        // (translateEntryText) keeps it first.
        let chrome = null;
        const offline = typeof navigator !== 'undefined' && navigator && navigator.onLine === false;
        if (options.provider === 'chrome' || (offline && !options.provider)) {
            try { chrome = (await _resolveProviders(src, tgt, options))[0]; } catch (_) { chrome = null; }
            if (!chrome || chrome.name !== 'chrome') chrome = null;
        }
        if (chrome || options.provider === 'chrome') {
            stats.lane = 'device';
            const q = createConcurrentQueue({ concurrency: FALLBACK_CONCURRENCY });
            const res = await q.run(list, (text) => translateEntryText(text, src, tgt, signal, options), {
                signal: signal,
                onProgress: (p) => { if (onProgress) onProgress({ done: p.done, total: p.total, frac: p.done / p.total }); },
            });
            if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
            for (let i = 0; i < list.length; i++) {
                const r = res[i];
                results[i] = (r && !r.__error) ? r
                    : { text: list[i] || '', provider: 'chrome', reason: 'unknown', err_msg: String((r && r.__error && r.__error.message) || '').slice(0, 240) };
                if (results[i].reason) stats.failed++;
            }
            return results;
        }

        // ── plan: what actually has to be translated ────────────────────
        const fetchFn = options.fetch || (typeof fetch !== 'undefined' ? fetch : null);
        const plans = new Array(list.length);
        const uniq = new Map();          // core → { core, out, err, provider }
        const order = [];
        let totalChars = 0;
        for (let i = 0; i < list.length; i++) {
            const text = list[i];
            if (!text || !String(text).trim()) { plans[i] = null; continue; }
            const plan = _planText(String(text), options.batchQMaxChars || BATCH_Q_MAX_CHARS);
            plans[i] = plan;
            for (const seg of plan) {
                if (seg.core === undefined || uniq.has(seg.core)) continue;
                const item = { core: seg.core, out: undefined, err: null, provider: null };
                uniq.set(seg.core, item);
                order.push(item);
                totalChars += seg.core.length;
            }
        }
        let doneChars = 0;
        function settled(item) {
            doneChars += item.core.length;
            if (onProgress) {
                onProgress({ done: doneChars, total: totalChars, frac: totalChars ? Math.min(1, doneChars / totalChars) : 1 });
            }
        }

        // ── 2. Google batch ─────────────────────────────────────────────
        const useBatch = !!fetchFn && options.batch !== false && options.provider !== 'mymemory';
        if (useBatch && order.length) {
            stats.lane = 'batch';
            const batches = _packBatches(order,
                options.batchMaxChars || BATCH_MAX_CHARS, options.batchMaxItems || BATCH_MAX_ITEMS);
            const delays = options.batchRetryDelaysMs || BATCH_RETRY_DELAYS_MS;
            let batchDead = false, shapeMisses = 0;
            const q = createConcurrentQueue({ concurrency: options.batchConcurrency || BATCH_CONCURRENCY });
            await q.run(batches, async (batch) => {
                if (batchDead) return;
                stats.batches++;
                for (let attempt = 0; ; attempt++) {
                    if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
                    stats.requests++;
                    try {
                        const out = await _googleBatchOnce(batch.map((it) => it.core), src, tgt, signal, fetchFn, options.batchTimeoutMs);
                        for (let k = 0; k < batch.length; k++) {
                            // An empty answer to a text that has words is not a
                            // translation (seen 2026-10-22 on German cells sent
                            // as English): the tool would store "" as done and the
                            // document would keep the original without saying so.
                            // It goes text by text, like any other miss.
                            if (!String(out[k]).trim()) { stats.empty++; continue; }
                            batch[k].out = out[k];
                            batch[k].provider = 'google';
                            settled(batch[k]);
                        }
                        return;
                    } catch (err) {
                        if (err && err.name === 'AbortError') throw err;
                        // A shape we cannot read will not read better on a
                        // second try: this batch goes text by text, and a door
                        // that answers nonsense twice is shut for the run.
                        if (err && err.reason === 'unknown') {
                            if (++shapeMisses >= 2) batchDead = true;
                            break;
                        }
                        // A request that ran into its deadline has already cost
                        // half a minute; an endpoint that swallows requests is
                        // not asked again.
                        const timedOut = /^timeout/.test(String((err && err.message) || ''));
                        if (batchDead || timedOut || attempt >= delays.length) { batchDead = true; break; }
                        stats.retries++;
                        status('retrying');
                        await sleep(delays[attempt], signal);
                        status('working');
                    }
                }
                stats.batchFailed++;
            }, { signal: signal });
            if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
        }

        // ── 2b. what the batch door left: the browser's own translator ──
        // No quota and no address to refuse — when the batch door is shut it
        // is the better next step than a thousand per-text requests.
        const leftover = order.filter((it) => it.out === undefined);
        if (leftover.length && !options.provider && !(signal && signal.aborted)) {
            let device = null;
            try { device = (await _resolveProviders(src, tgt, options))[0]; } catch (_) { device = null; }
            if (device && device.name === 'chrome') {
                const qd = createConcurrentQueue({ concurrency: FALLBACK_CONCURRENCY });
                await qd.run(leftover, async (item) => {
                    try {
                        const got = await device.translatePart(item.core, signal);
                        if (got != null && String(got).trim()) {
                            item.out = got;
                            item.provider = 'chrome';
                            stats.deviceTexts = (stats.deviceTexts || 0) + 1;
                            settled(item);
                        }
                    } catch (err) {
                        if (err && err.name === 'AbortError') throw err;
                    }
                }, { signal: signal });
                if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
                if (stats.deviceTexts && stats.deviceTexts === leftover.length && leftover.length === order.length) stats.lane = 'device';
            }
        }

        // ── 3. text by text, behind a circuit breaker ───────────────────
        const left = order.filter((it) => it.out === undefined);
        if (left.length) {
            if (!stats.lane) stats.lane = 'single';
            stats.fallbackTexts = left.length;
            const chain = [];
            if (fetchFn && options.provider !== 'mymemory') chain.push(_buildGoogleProvider(src, tgt, options));
            if (fetchFn && options.provider !== 'google') chain.push(_buildMyMemoryProvider(src, tgt, options));
            const health = new Map(chain.map((p) => [p.name, { until: 0, strikes: 0 }]));
            const cool = options.cooldownMs || FALLBACK_COOLDOWN_MS;
            let patience = options.patienceMs != null ? options.patienceMs : FALLBACK_PATIENCE_MS;
            let gate = null, lastReason = 'network', lastMsg = 'no provider available';

            async function one(item) {
                for (;;) {
                    if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
                    const now = Date.now();
                    const live = chain.filter((p) => health.get(p.name).until <= now);
                    if (!live.length) {
                        // Everyone is resting. Wait for the first to wake — all
                        // workers share ONE wait, charged to the budget once.
                        if (gate) { await gate; continue; }
                        let soonest = Infinity;
                        for (const p of chain) soonest = Math.min(soonest, health.get(p.name).until);
                        const wait = soonest - now;
                        if (!chain.length || !isFinite(wait) || patience < wait) {
                            patience = 0;
                            item.err = { reason: lastReason, message: lastMsg };
                            return;
                        }
                        patience -= wait;
                        status('retrying');
                        gate = sleep(wait, signal).then(() => { gate = null; status('working'); }, (e) => { gate = null; throw e; });
                        await gate;
                        continue;
                    }
                    let failure = null;
                    for (const p of live) {
                        try {
                            stats.requests++;
                            const got = await p.translatePart(item.core, signal);
                            if (got == null || !String(got).trim()) throw new TranslationError('empty', 'empty translation');
                            item.out = got;
                            item.provider = p.name;
                            health.get(p.name).strikes = 0;
                            return;
                        } catch (err) {
                            if (err && err.name === 'AbortError') throw err;
                            failure = err;
                            const reason = err && err.reason;
                            if (ENDPOINT_DEAD_REASONS.has(reason)) {
                                lastReason = reason; lastMsg = String((err && err.message) || '');
                                const h = health.get(p.name);
                                h.strikes++;
                                // A spent daily quota does not come back within a run.
                                h.until = reason === 'quota' ? Infinity
                                    : Date.now() + Math.min(FALLBACK_COOLDOWN_MAX_MS, cool * Math.pow(2, h.strikes - 1));
                            }
                        }
                    }
                    // Every live provider refused THIS text. If that was an
                    // endpoint-level refusal the loop waits for a rested one;
                    // a one-off bad answer fails the text and nothing else.
                    if (failure && ENDPOINT_DEAD_REASONS.has(failure.reason)) continue;
                    item.err = { reason: (failure && failure.reason) || 'unknown', message: String((failure && failure.message) || '') };
                    return;
                }
            }

            const q2 = createConcurrentQueue({ concurrency: FALLBACK_CONCURRENCY });
            await q2.run(left, async (item) => { await one(item); settled(item); }, { signal: signal });
            if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
        }

        // ── back to one answer per text ─────────────────────────────────
        for (let i = 0; i < list.length; i++) {
            const plan = plans[i];
            if (!plan) { results[i] = { text: list[i] || '' }; continue; }
            let out = '', err = null, provider = null;
            for (const seg of plan) {
                if (seg.core === undefined) { out += seg.raw; continue; }
                const item = uniq.get(seg.core);
                if (item.out === undefined) { err = err || item.err || { reason: 'unknown', message: '' }; continue; }
                provider = provider || item.provider;
                out += seg.lead + stripInjectedZw(seg.core, item.out, tgt) + seg.trail;
            }
            if (err) {
                stats.failed++;
                results[i] = {
                    text: list[i], provider: provider || 'google',
                    reason: err.reason || 'unknown',
                    err_msg: String(err.message || '').slice(0, 240),
                    status: null,
                };
            } else {
                results[i] = { text: out, provider: provider || 'google' };
            }
        }
        return results;
    }

    // Concurrent bounded queue. Preserves input order in the returned array.
    // `processItem(item, index, signal)` → result (or throws).
    // Per-worker throttle via `perRequestDelayMs` prevents bursts even when
    // individual requests are fast.
    function createConcurrentQueue(cfg) {
        const concurrency = (cfg && cfg.concurrency) || 4;
        const perRequestDelayMs = (cfg && cfg.perRequestDelayMs) || 0;

        async function run(items, processItem, runOpts) {
            const signal = runOpts && runOpts.signal;
            const onProgress = runOpts && runOpts.onProgress;
            const total = items.length;
            const results = new Array(total);
            let nextIndex = 0, done = 0;

            async function worker() {
                while (true) {
                    if (signal && signal.aborted) return;
                    const i = nextIndex++;
                    if (i >= total) return;
                    try {
                        results[i] = await processItem(items[i], i, signal);
                    } catch (err) {
                        if (err && err.name === 'AbortError') return;
                        results[i] = { __error: err, item: items[i] };
                    }
                    done++;
                    if (onProgress) onProgress({ done, total, index: i });
                    if (perRequestDelayMs > 0 && !(signal && signal.aborted) && nextIndex < total) {
                        try { await sleep(perRequestDelayMs, signal); }
                        catch (e) { if (e.name === 'AbortError') return; throw e; }
                    }
                }
            }

            const workers = Array.from(
                { length: Math.min(concurrency, total) },
                () => worker()
            );
            await Promise.all(workers);
            return results;
        }

        return { run };
    }

    // Reset the per-pair provider cache. Tests call this between cases that
    // install a mock Translator global so a cached probe doesn't leak.
    function _resetProviderCache() {
        _providerCache.clear();
        _providerSettled.clear();
    }

    global.TranslatorEngine = {
        // Constants
        API_URL,
        API_MAX_CHARS,
        MYMEMORY_EMAIL,
        GOOGLE_URL,

        // Pure helpers
        detectLanguage,
        detectLanguageInfo,
        toChromeCode,
        toGoogleCode,
        splitByTags,
        splitForApi,
        decodeEntities,
        classifyHttpError,
        _parseGoogleResponse,

        // I/O
        TranslationError,
        translateSegment,
        translateEntryText,
        translateTexts,
        GOOGLE_BATCH_URL,
        _parseGoogleBatch,
        detectLanguageAsync,
        identifyLanguage,
        normalizeLangCode,
        resolveProviderName,
        createConcurrentQueue,

        // Test hooks
        _sleep: sleep,
        _resetProviderCache,
    };
}(typeof window !== 'undefined' ? window : globalThis));
